import EditIcon from "@/assets/edit.svg";
import EyeIcon from "@/assets/eye.svg";
import FavIcon from "@/assets/heart.svg";
import MsgIcon from "@/assets/msg-none.svg";
import TrashIcon from "@/assets/trash.svg";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, Send } from "lucide-react";
import { useState } from "react";
import { CircleFlag } from "react-circle-flags";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import type { RouterOutputs } from "@/lib/trpc";
import type { Routes } from "@/routes";

import { ArchiveTournamentConfirmationModal } from "@/components/tournaments/tournament-infos";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useMe } from "@/context/user-context";
import { useBreakpoints } from "@/hooks/use-breakpoints";
import { useOpenMessageThread } from "@/hooks/use-open-message-thread";
import { useTranslationUtils } from "@/hooks/use-translation-utils";
import { useTRPC } from "@/lib/trpc";
import { cn, formatDate, formatYear } from "@/lib/utils";
import { routes } from "@/routes";

export function TournamentListItem({ tournament, fromPath }: { tournament: RouterOutputs["tournament"]["getTournamentList"][number]; fromPath?: Routes["favorites"] | Routes["tournaments"] }) {
  const { t } = useTranslation(["common", "profile", "tournaments"]);
  const { getCountryName } = useTranslationUtils();
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const breakpoint = useBreakpoints();
  const today = new Date().toISOString().split("T")[0];
  const { user } = useMe();
  const navigate = useNavigate();
  const [confirmationModalState, updateConfirmationModalState] = useState<"open" | "close">("close");
  const openMessageThread = useOpenMessageThread();
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { data: player } = useQuery(trpc.player.getPlayer.queryOptions(
    { playerId: user.id },
    { enabled: user.role === "player" },
  ));

  const { mutate, isPending } = useMutation(trpc.tournament.archiveTournament.mutationOptions({
    onSuccess: () => {
      queryClient.invalidateQueries(trpc.tournament.getTournamentList.queryFilter());
      navigate(routes.tournaments);
    },
  }));

  function archiveTournament() {
    mutate({ id: tournament.id });
  }

  const { mutate: publishMutation, isPending: isPublishing } = useMutation(trpc.tournament.publishTournament.mutationOptions({
    onSuccess: () => {
      queryClient.invalidateQueries(trpc.tournament.getTournamentList.queryFilter());
    },
  }));

  const { mutate: updateFavoriteMutation } = useMutation(trpc.player.updateFavoritesTournaments.mutationOptions({
    onSuccess: () => {
      queryClient.invalidateQueries(trpc.player.getPlayer.queryFilter());
      queryClient.invalidateQueries(trpc.player.getPlayerFavoriteTournament.queryFilter());
    },
  }));

  const isTournamentAlreadyFav = player?.favoritesTournaments.includes(tournament.id);

  const updateFavorite = () => {
    updateFavoriteMutation({
      tournamentId: tournament.id,
    });
  };

  function upsertThread() {
    openMessageThread(tournament.clubRepresentativeId);
  }

  return (
    <>
      <div
        className={cn(
          "grid grid-cols-2 gap-2.5 rounded-[18px] border border-neutral-400 bg-white px-2.5 py-4 sm:grid-cols-4 md:rounded-none md:border-x-0 md:border-t-0 md:first:rounded-t-[18px] md:last:rounded-b-[18px] md:last:border-b-0 lg:grid-cols-[300px,55px,80px,140px,auto,120px]",
          today > tournament.endDate.split(" ")[0] && tournament.clubRepresentativeId !== user.id && "pointer-events-none cursor-default opacity-40",
          tournament.status === "draft" && "bg-orange-100/40",
        )}
        key={tournament.id}
      >
        <div
          className="order-1 col-span-2 flex cursor-pointer items-center gap-3 lg:col-span-1"
          onClick={() => navigate(routes.getTournamentPath(tournament.id), { state: { fromPath } })}
        >
          <img
            src={tournament.imgUrl}
            alt={`Profil de ${tournament.name}`}
            className="pointer-events-none size-14 shrink-0 select-none rounded-xl object-cover"
          />

          <div className="flex min-w-0 flex-col gap-1">
            <div className="flex min-w-0 items-center gap-2">
              <h1 className="truncate text-lg font-semibold uppercase">{tournament.name}</h1>
              {/* Brouillon préparé sans abonnement : visible du seul club, avec
                  l'action qui lui permet de le rendre public. */}
              {tournament.status === "draft" && (
                <Badge variant="orange" className="shrink-0">{t("tournaments:draft")}</Badge>
              )}
            </div>

            <div className="flex items-center gap-2">
              {tournament.tournamentPlayers.length > 0 && (
                <div className="flex items-center gap-1.5">
                  {tournament.tournamentPlayers.map(player => (
                    player.gender === "male"
                      ? (
                          <Badge key={player.id} variant="blue" size="compact">
                            {t("common:man").charAt(0).toUpperCase()}
                          </Badge>
                        )
                      : (
                          <Badge key={player.id} variant="purple" size="compact">
                            {t("common:woman").charAt(0).toUpperCase()}
                          </Badge>
                        )
                  ))}
                </div>
              )}

              <div className="flex min-w-0 items-center gap-1.5 rounded-full bg-neutral-100 p-0.5 lg:hidden">
                <CircleFlag countryCode={tournament.country.toLowerCase()} className="size-4 rounded-full" />
                <span className="block truncate pr-3 text-sm font-medium">
                  {`${tournament.city}, ${getCountryName(tournament.country)}`}
                </span>
              </div>

              <span className="hidden truncate text-sm font-medium text-neutral-500 lg:block">
                {`${tournament.city}, ${getCountryName(tournament.country)}`}
              </span>
            </div>
          </div>
        </div>

        <div className="hidden items-center justify-center lg:order-2 lg:flex">
          <div className="flex h-fit w-auto max-w-48 shrink-0 items-center gap-1.5 rounded-full bg-neutral-100 p-0.5">
            <CircleFlag countryCode={tournament.country.toLowerCase()} className="size-4 rounded-full" />
            <span className="pr-2 text-sm font-medium">
              {tournament.country.toUpperCase()}
            </span>
          </div>
        </div>

        <div className="order-3 flex items-center justify-end sm:order-4 sm:justify-center lg:order-3 lg:col-span-1">
          {tournament.winnerPrize
            ? (
                <div className="flex flex-col">
                  <span className="block text-sm lg:hidden">
                    {t("tournaments:winner-prize")}
                  </span>
                  <span className="block text-nowrap text-end text-sm font-semibold sm:text-center">
                    {`${tournament.winnerPrize?.toLocaleString()} €`}
                  </span>
                </div>
              )
            : (
                <span className="w-full pr-2 text-end font-medium sm:text-center">-</span>
              )}
        </div>

        <div className="order-2 flex items-center sm:order-3 sm:col-span-1 sm:justify-center lg:order-4 lg:col-span-1">
          {tournament.tournamentSurface
            ? (
                <span className={cn(
                  " text-nowrap rounded-md px-1.5 py-2 text-center text-sm font-medium leading-4",
                  tournament.tournamentSurface?.includes("clay") && "bg-orange-300",
                  tournament.tournamentSurface?.includes("hard") && "bg-blue-200",
                )}
                >
                  { t(`profile:custom-surface.${tournament.tournamentSurface}`)}
                </span>
              )
            : (
                <span className="w-full pl-2 font-medium sm:text-center">-</span>
              )}
        </div>

        <div className="order-4 col-span-2 flex items-center gap-2 rounded-xl border sm:order-2 lg:order-5 lg:col-span-1 lg:border-0">
          <div className="flex w-full flex-col items-center text-nowrap py-2 leading-5">
            <span className="text-sm font-semibold">
              {formatDate(tournament.startDate, locale)}
            </span>
            <span className="text-sm font-medium text-neutral-500">{formatYear(tournament.startDate, locale)}</span>
          </div>

          <ArrowRight className="size-6 shrink-0 text-orange-500" />

          <div className="flex w-full flex-col items-center text-nowrap py-2 leading-5">
            <span className="text-sm font-semibold">
              {formatDate(tournament.endDate, locale)}
            </span>
            <span className="text-sm font-medium text-neutral-500">{formatYear(tournament.endDate, locale)}</span>
          </div>
        </div>

        <div className="order-5 col-span-2 flex items-center gap-1 sm:col-span-2 sm:items-end lg:order-6 lg:col-span-1 lg:justify-end">
          {user.role === "club"
          && (
            <>
              <Button
                variant="outline"
                size={["lg", "xl"].includes(breakpoint) ? "compact" : "default"}
                onClick={() => navigate(routes.getTournamentPath(tournament.id))}

              >
                <EditIcon />
              </Button>

              <Button
                variant="outline"
                size={["lg", "xl"].includes(breakpoint) ? "compact" : "default"}
                onClick={() => updateConfirmationModalState("open")}
              >
                <TrashIcon />
              </Button>
            </>
          )}
          {user.role === "player"
          && (
            <>
              <Button
                variant="outline"
                size={["lg", "xl"].includes(breakpoint) ? "compact" : "default"}
                className="active:border-neutral-300"
                onClick={updateFavorite}
              >
                <FavIcon
                  className={
                    isTournamentAlreadyFav
                      ? "fill-[#FE5000] "
                      : "fill-white "
                  }
                />
              </Button>
              <Button
                variant="outline"
                size={["lg", "xl"].includes(breakpoint) ? "compact" : "default"}
                onClick={upsertThread}
              >
                <MsgIcon />
              </Button>
            </>
          )}
          {/* Brouillon : le club abonné publie en un clic, celui qui ne l'est pas
              est envoyé vers les abonnements sans perdre son travail. */}
          {/* Publication directe pour un club abonné qui a gardé des brouillons.
              Un club sans abonnement passe par le bandeau de la page du tournoi. */}
          {tournament.status === "draft" && user.role === "club" && user.plan && (
            <Button
              className="w-full lg:w-fit"
              size={["lg", "xl"].includes(breakpoint) ? "compact" : "default"}
              onClick={() => publishMutation({ tournamentId: tournament.id })}
              loading={isPublishing}
              aria-label={t("tournaments:publish")}
              title={t("tournaments:publish")}
            >
              <Send className="size-4" />
              <span className="lg:hidden">{t("tournaments:publish")}</span>
            </Button>
          )}
          <Button
            className="w-full lg:w-fit"
            size={["lg", "xl"].includes(breakpoint) ? "compact" : "default"}
            onClick={() => navigate(routes.getTournamentPath(tournament.id), { state: { fromPath } })}
          >
            <EyeIcon />
            <span className="lg:hidden">
              {t("common:go-to-tournament-page")}
            </span>
          </Button>
        </div>
      </div>

      <ArchiveTournamentConfirmationModal
        modalOpen={confirmationModalState === "open"}
        closeModal={() => updateConfirmationModalState("close")}
        onClick={archiveTournament}
        isPending={isPending}
      />
    </>
  );
}
